import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NewChatDialog from "./NewChatDialog";
import { toast } from "sonner";
import { Users, GitBranch, MessageSquarePlus, ChevronRight } from "lucide-react";

const TABS = [
  ["all", "chats_tab_all"],
  ["circles", "chats_tab_circles"],
  ["direct", "chats_tab_direct"],
  ["shared", "chats_tab_shared"],
];

export default function Chats() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("chats"));
  const [tab, setTab] = useState("all");
  const [circles, setCircles] = useState([]);
  const [dms, setDms] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [newChatOpen, setNewChatOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, d, s] = await Promise.all([
      api.get("/circles").then((r) => r.data).catch(() => []),
      api.get("/messages/inbox/list").then((r) => r.data).catch(() => []),
      api.get("/collaboration/shared-chat").then((r) => r.data).catch(() => []),
    ]);
    setCircles(Array.isArray(c) ? c : []);
    const dedupDms = Array.isArray(d)
      ? Array.from(new Map(d.filter((x) => x?.user_id).map((x) => [x.user_id, x])).values())
      : [];
    setDms(dedupDms);
    setShared(Array.isArray(s) ? s : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const join = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    try { await api.post("/circles/join", { invitation_code: c }); setCode(""); await load(); }
    catch (e) { toast.error(apiError(e) || t("chats_invalid_code")); }
  };

  const showCircles = tab === "all" || tab === "circles";
  const showDMs = tab === "all" || tab === "direct";
  const showShared = tab === "all" || tab === "shared";
  const empty = !circles.length && !dms.length && !shared.length;

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="chats-screen">
      <PageHeader
        eyebrow={t("nav.group.start")}
        title={t("chats")}
        action={
          <Button onClick={() => setNewChatOpen(true)} className="rounded-full" data-testid="chats-new-btn">
            <MessageSquarePlus className="mr-1.5 h-4 w-4" /> {t("new_chat")}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`chats-tab-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{t(label)}</button>
        ))}
      </div>

      {loading ? <Spinner label={t("common.loading")} /> : (
        <div className="space-y-6">
          {empty ? <p className="py-12 text-center text-sm italic text-muted-foreground">{t("no_conversations")}</p> : null}

          {showCircles ? (
            <section className="space-y-2" data-testid="chats-section-circles">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("my_circles")}</h2>
              <div className="flex items-center gap-2 rounded-2xl border border-border bg-primary/5 p-3">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("chats_join_placeholder")} data-testid="chats-join-code" className="h-9 flex-1 bg-card uppercase" onKeyDown={(e) => e.key === "Enter" && join()} />
                <Button onClick={join} className="h-9 rounded-full" data-testid="chats-join-btn">{t("join")}</Button>
              </div>
              {circles.length === 0 ? (
                <p className="px-1 text-xs italic text-muted-foreground">{t("circles_empty_sub")}</p>
              ) : circles.map((c) => (
                <button key={c.id} onClick={() => navigate("/app/circles")} data-testid={`chats-circle-${c.id}`}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary"><Users className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground">{c.name}</span>
                    <span className="block text-xs text-muted-foreground">{(c.members?.length ?? 0)} {t("members")}</span>
                  </span>
                  {c.unread_count > 0 ? <span data-testid={`chats-circle-unread-${c.id}`} className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">{c.unread_count > 99 ? "99+" : c.unread_count}</span> : null}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </section>
          ) : null}

          {showShared && shared.length > 0 ? (
            <section className="space-y-2" data-testid="chats-section-shared">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("shared_chats")}</h2>
              {shared.map((c) => (
                <button key={c.id} onClick={() => navigate(`/app/shared-chat/${c.id}`)} data-testid={`chats-shared-${c.id}`}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground"><GitBranch className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground">{c.coach_a_name} + {c.coach_b_name}</span>
                    <span className="block text-xs text-muted-foreground">{t("three_way_care")}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </section>
          ) : null}

          {showDMs && dms.length > 0 ? (
            <section className="space-y-2" data-testid="chats-section-direct">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("direct_messages")}</h2>
              {dms.map((d) => (
                <button key={d.user_id} onClick={() => navigate(`/app/messages/${d.user_id}`, { state: { name: d.user_name, photo: d.user_photo } })} data-testid={`chats-dm-${d.user_id}`}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:bg-accent">
                  {d.user_photo ? <img src={d.user_photo} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">{(d.user_name || "·")[0]}</span>}
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${d.unread_count > 0 ? "font-bold text-foreground" : "font-semibold text-foreground"}`}>{d.user_name}</span>
                    <span className={`block truncate text-xs ${d.last_deleted ? "italic text-muted-foreground/70" : d.unread_count > 0 ? "font-medium text-foreground" : "text-muted-foreground"}`}>{d.last_deleted ? t("message_deleted") : d.last_body}</span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    {d.last_at ? <span className="text-[11px] text-muted-foreground">{shortDate(d.last_at)}</span> : null}
                    {d.unread_count > 0 ? <span data-testid={`chats-dm-unread-${d.user_id}`} className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">{d.unread_count > 99 ? "99+" : d.unread_count}</span> : null}
                  </span>
                </button>
              ))}
            </section>
          ) : null}
        </div>
      )}

      <NewChatDialog open={newChatOpen} onClose={() => setNewChatOpen(false)} />
    </div>
  );
}
